/**
 * E10 — Unified Application Detail: AttributeField component
 *
 * Renders a single application attribute with:
 *   - Friendly label from the attribute dictionary
 *   - Plain-language (?) tooltip from the description
 *   - Copy-to-clipboard button when copyable=true
 *   - Format-aware value rendering (date, url, monospace, boolean, badge)
 *
 * Usage:
 *   <AttributeField attrKey="entraAppId" value={application.entraAppId} />
 */
import { useState } from 'react'
import { Text, TooltipHost, DirectionalHint, Icon } from '@fluentui/react'
import { APPLICATION_ATTRIBUTES } from '../config/attributeDictionary'
import type { ApplicationAttributeKey, AttributeDef } from '../config/attributeDictionary'

// --------------------------------------------------------------------------
// Helpers
// --------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  } catch {
    return iso
  }
}

// --------------------------------------------------------------------------
// CopyButton
// --------------------------------------------------------------------------

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // clipboard may be unavailable in some iframe contexts
    }
  }

  return (
    <TooltipHost
      content={copied ? 'Copied!' : 'Copy to clipboard'}
      directionalHint={DirectionalHint.topCenter}
    >
      <button
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 4px',
          color: copied ? '#107c10' : '#605e5c',
          display: 'inline-flex',
          alignItems: 'center',
          verticalAlign: 'middle',
          marginLeft: 4,
        }}
      >
        <Icon iconName={copied ? 'CheckMark' : 'Copy'} style={{ fontSize: 13 }} />
      </button>
    </TooltipHost>
  )
}

// --------------------------------------------------------------------------
// InfoTooltip
// --------------------------------------------------------------------------

function InfoTooltip({ description }: { description: string }) {
  return (
    <TooltipHost
      content={description}
      directionalHint={DirectionalHint.topCenter}
      styles={{ root: { display: 'inline-flex', alignItems: 'center', marginLeft: 4 } }}
      tooltipProps={{
        styles: {
          content: { maxWidth: 300, fontSize: 12, lineHeight: '1.5' },
        },
      }}
    >
      <Icon
        iconName="Info"
        aria-label="More information"
        style={{ fontSize: 12, color: '#605e5c', cursor: 'default', verticalAlign: 'middle' }}
      />
    </TooltipHost>
  )
}

// --------------------------------------------------------------------------
// Value renderers
// --------------------------------------------------------------------------

function renderValue(
  format: string | undefined,
  value: unknown,
): React.ReactNode {
  if (value == null || value === '') {
    return <Text style={{ color: '#a19f9d', fontStyle: 'italic' }}>Not set</Text>
  }

  switch (format) {
    case 'monospace':
      return (
        <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>{String(value)}</Text>
      )

    case 'date':
      return <Text>{formatDate(String(value))}</Text>

    case 'url':
      return (
        <a
          href={String(value)}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#0078d4', wordBreak: 'break-all' }}
        >
          {String(value)}
        </a>
      )

    case 'boolean':
      return (
        <span style={{
          display: 'inline-block',
          fontSize: 12,
          fontWeight: 600,
          padding: '2px 8px',
          borderRadius: 4,
          background: value ? '#dff6dd' : '#fde7e9',
          color: value ? '#107c10' : '#a4262c',
        }}>
          {value ? 'Yes' : 'No'}
        </span>
      )

    case 'badge':
      return (
        <span style={{
          display: 'inline-block',
          fontSize: 12,
          fontWeight: 500,
          padding: '2px 8px',
          borderRadius: 4,
          background: '#f3f2f1',
          color: '#323130',
        }}>
          {String(value)}
        </span>
      )

    default:
      return <Text>{String(value)}</Text>
  }
}

// --------------------------------------------------------------------------
// AttributeField
// --------------------------------------------------------------------------

interface AttributeFieldProps {
  attrKey: ApplicationAttributeKey
  value: unknown
  /** Override the label from the dictionary (rare). */
  labelOverride?: string
}

export function AttributeField({ attrKey, value, labelOverride }: AttributeFieldProps) {
  const def = APPLICATION_ATTRIBUTES[attrKey] as AttributeDef
  const label = labelOverride ?? def.label
  const stringValue = value != null ? String(value) : ''

  return (
    <div>
      {/* Label row */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>{label}</Text>
        <InfoTooltip description={def.description} />
      </div>

      {/* Value row */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {renderValue(def.format, value)}
        {def.copyable && stringValue && (
          <CopyButton value={stringValue} />
        )}
      </div>
    </div>
  )
}

// --------------------------------------------------------------------------
// AttributeSection — renders a labelled group of AttributeFields
// --------------------------------------------------------------------------

export interface AttributeSectionProps {
  title: string
  children: React.ReactNode
  /** Source badge shown next to the title (e.g. "App Registration", "Enterprise App") */
  sourceBadge?: string
}

export function AttributeSection({ title, children, sourceBadge }: AttributeSectionProps) {
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
        <h3 className="card-title" style={{ margin: 0 }}>{title}</h3>
        {sourceBadge && (
          <span style={{
            fontSize: 11,
            fontWeight: 500,
            padding: '2px 6px',
            borderRadius: 4,
            background: '#f3f2f1',
            color: '#605e5c',
          }}>
            {sourceBadge}
          </span>
        )}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '16px 24px' }}>
        {children}
      </div>
    </div>
  )
}
